import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { mediaQueries } from "../styles/media.js";
import ReferralCallToAction from "../components/ReferralCallToAction.js";

function DashboardLayout({ children, showMenu, selectedNavItem, showNav, referralLink, navItems }) {
  return (
    <DashboardGrid>
      <DashboardSidebar>
        <Aside showMenu={showMenu}>
          <CTAWrapper>
            <ReferralCallToAction referralLink={referralLink} />
          </CTAWrapper>
          <Nav role="navigation">
            <NavList>
              {navItems.map(({ key, name }) => (
                <NavListItem key={key}>
                  <NavListLink
                    selected={selectedNavItem === key}
                    aria-current={selectedNavItem === key ? "page" : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      showNav(key);
                    }}
                  >
                    {name}
                  </NavListLink>
                </NavListItem>
              ))}
            </NavList>
          </Nav>
        </Aside>
      </DashboardSidebar>
      <DashboardContent>{children}</DashboardContent>
    </DashboardGrid>
  );
}

export default DashboardLayout;

DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
  showMenu: PropTypes.bool.isRequired,
  selectedNavItem: PropTypes.string.isRequired,
  showNav: PropTypes.func.isRequired,
  referralLink: PropTypes.string,
  navItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

const DashboardContent = styled.main`
  height: 100%;
  /* A grid item's minimum is its content's width, so without this a wide (nowrap) table widens
     the page rather than scrolling inside its own container. */
  min-width: 0;
  ${mediaQueries.lg} {
    padding-top: 4rem;
    height: calc(100vh - 4rem);
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: auto;
  ${mediaQueries.lg} {
    grid-template-columns: 18rem auto;
  }
`;

const DashboardSidebar = styled.div`
  /* Becomes 18rem wide on large screens */
  padding-top: 4rem;
`;

const Aside = styled.aside`
  background-color: var(--juno-color-purple);

  display: none;
  width: 100%;
  height: 100%;

  ${mediaQueries.lg} {
    display: block;
    position: fixed;
    width: 18rem;
  }

  ${(props) => (props.showMenu ? "display: block;" : "display: none;")}
  ${(props) => props.showMenu && "position: fixed;"}
  z-index: 1;
`;

// Carries the gap below the box so the nav sits in the same place whichever variant renders.
const CTAWrapper = styled.div`
  margin: 1.5rem 1.5rem 2rem;
`;

const Nav = styled.nav`
  width: 100%;
  transform: translateZ(0);
`;

const NavList = styled.ul`
  font-size: var(--juno-font-size-body-md);
  color: var(--juno-color-white);
  list-style: none;

  ${mediaQueries.lg} {
    margin-right: 0.5rem;
    margin-bottom: 3rem;
  }
  padding-left: 0;
`;

const NavListItem = styled.li`
  display: flex;
  align-items: center;
  width: 100%;

  margin-top: 1rem;
  margin-bottom: 1rem;

  svg {
    margin-right: 1rem;
  }
`;

const NavListLink = styled.a`
  display: flex;
  width: calc(100% - 2rem);
  padding: 0.75rem 1rem 0.75rem 1.5rem;

  border: 0 !important;
  color: ${(props) => (props.selected ? "var(--juno-color-white)" : "var(--juno-color-pale-purple)")} !important;
  background: ${(props) => (props.selected ? "var(--juno-color-purple)" : "transparent")};
  font-weight: ${(props) => (props.selected ? "var(--juno-font-weight-bold)" : "var(--juno-font-weight-regular)")};

  &:focus-visible {
    outline: var(--juno-focus-outline-width) solid var(--juno-color-focus);
    outline-offset: var(--juno-focus-outline-offset);
    color: var(--juno-color-pale-purple) !important;
    background: var(--juno-color-purple);
  }

  &:hover {
    background: ${(props) => (props.selected ? "var(--juno-color-purple)" : "var(--juno-color-pale-purple)")};
    border-start-end-radius: 1rem;
    border-end-end-radius: 1rem;

    color: ${(props) => (props.selected ? "var(--juno-color-white)" : "var(--juno-color-purple)")} !important;
    cursor: ${(props) => (props.selected ? "default" : "pointer")};
  }
`;
